// AGC: Single source of truth for main nav items used by BottomNav and DesktopSidebar.
// AGC: Labels, icons, permission flags — define once, render differently per shell.
import React from 'react'
import { LayoutDashboard, MapPin, CheckSquare, CloudSun, DollarSign, Package } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { usePermissions } from '../../hooks/usePermissions'
import type { NavTab } from '../../types'

export type { NavTab }

export interface MainNavItem {
  id: NavTab
  label: string
  icon: React.ReactNode
  enabled: boolean
}

const isTablet = import.meta.env.VITE_TABLET_MODE === 'true'
const iconSize = isTablet ? 'w-7 h-7' : 'w-5 h-5'

// AGC: Hook returns nav items with permission gating. Tablet mode adds finances + inventory.
export const useMainNavItems = (): MainNavItem[] => {
  const { t } = useTranslation()
  const permissions = usePermissions()

  const items: MainNavItem[] = [
    { id: 'dashboard', label: t('tabs.dashboard'), icon: React.createElement(LayoutDashboard, { className: iconSize }), enabled: true },
    { id: 'fields',    label: t('tabs.lots'),      icon: React.createElement(MapPin,          { className: iconSize }), enabled: permissions.canViewFields },
  ]

  if (isTablet) {
    items.push(
      { id: 'finances',  label: t('tabs.finances'),  icon: React.createElement(DollarSign, { className: iconSize }), enabled: permissions.canViewFinancial },
    )
  }

  if (isTablet) {
    items.push(
      { id: 'inventory', label: t('tabs.inventory'), icon: React.createElement(Package, { className: iconSize }), enabled: true },
    )
  }

  items.push(
    { id: 'tasks',   label: t('tabs.tasks'),   icon: React.createElement(CheckSquare, { className: iconSize }), enabled: permissions.canViewTasks },
    { id: 'weather', label: t('tabs.weather'), icon: React.createElement(CloudSun,    { className: iconSize }), enabled: permissions.canViewWeather },
  )

  return items
}
